import React, { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { SearchableDropdown } from '@/components';
import { BUSINESS_UNIT_OPTIONS } from '@/types/common';
import {
  ConditionField,
  ConditionOperator,
  CONDITION_FIELD_LABEL,
  CONDITION_FIELD_UNIT,
  CONDITION_OPERATOR_LABEL,
  isMembershipConditionField,
  MembershipConditionField,
  PackageCondition,
  PackageFormValues,
  PACKAGE_IMAGE_DIMENSIONS,
  PACKAGE_IMAGE_UPLOAD_TYPE,
  RentType,
  RENT_TYPE_LABEL,
} from '@/types/package';
import ImageUpload from '@/components/ImageUpload/ImageUpload';
import { PackageFormErrors } from '../packageForm';
import {
  AddRowButton,
  FieldError,
  FieldHelper,
  FieldLabel,
  FormCard,
  inputClass,
  RupiahInput,
  SelectField,
  TextareaField,
  ToggleRow,
} from './PackageFormControls';
import { PackageStockPreviewNote } from './PackageStockPreview';

interface PackageDetailSectionProps {
  values: PackageFormValues;
  errors: PackageFormErrors;
  onChange: <K extends keyof PackageFormValues>(
    field: K,
    value: PackageFormValues[K]
  ) => void;
  modelOptions: Array<{ value: string; label: string }>;
  isLoadingModels?: boolean;
  /** Clients (fleet providers) selectable in a Client condition row. */
  clientOptions: Array<{ value: string; label: string }>;
  isLoadingClients?: boolean;
  /** Typing in the Client dropdown searches the provider list server-side. */
  onClientSearch?: (search: string) => void;
  /** Lets the page block submit while an artwork upload is still in flight. */
  onUploadingChange?: (isUploading: boolean) => void;
  /** Opens the units behind the stock count; absent until a model is picked. */
  onViewUnits?: () => void;
}

const typeOptions = Object.values(RentType).map((type) => ({
  value: type,
  label: RENT_TYPE_LABEL[type],
}));

const operatorOptions = Object.values(ConditionOperator).map((operator) => ({
  value: operator,
  label: CONDITION_OPERATOR_LABEL[operator],
}));

const conditionFieldOptions = Object.values(ConditionField).map((field) => ({
  value: field,
  label: CONDITION_FIELD_LABEL[field],
}));

export const PackageDetailSection: React.FC<PackageDetailSectionProps> = ({
  values,
  errors,
  onChange,
  modelOptions,
  isLoadingModels = false,
  clientOptions,
  isLoadingClients = false,
  onClientSearch,
  onUploadingChange,
  onViewUnits,
}) => {
  // Named in the empty state when known, so the blast radius is concrete
  // ("semua unit Honda PCX") rather than abstract.
  const selectedModelLabel = useMemo(() => {
    const selected = modelOptions.find(
      (option) => option.value === values.modelID
    );
    return selected ? `model ${selected.label}` : 'model ini';
  }, [modelOptions, values.modelID]);

  /**
   * What a membership row's multi-select shows, one entry per dimension. The
   * record is keyed by `MembershipConditionField`, so a dimension added to
   * `MEMBERSHIP_CONDITION_FIELDS` has to be given its options here before this
   * compiles.
   */
  const membershipDropdowns: Record<
    MembershipConditionField,
    {
      options: Array<{ value: string; label: string }>;
      placeholder: string;
      emptyMessage: string;
      loading: boolean;
    }
  > = useMemo(
    () => ({
      [ConditionField.BUSINESS_UNIT]: {
        options: BUSINESS_UNIT_OPTIONS,
        placeholder: 'Pilih business unit',
        emptyMessage: 'Tidak ada business unit',
        loading: false,
      },
      [ConditionField.CLIENT]: {
        options: clientOptions,
        placeholder: 'Cari atau pilih client',
        emptyMessage: isLoadingClients
          ? 'Memuat client...'
          : 'Tidak ada client',
        loading: isLoadingClients,
      },
    }),
    [clientOptions, isLoadingClients]
  );

  const updateCondition = (
    key: string,
    patch: Partial<Omit<PackageCondition, 'key'>>
  ) => {
    onChange(
      'conditions',
      values.conditions.map((condition) =>
        condition.key === key ? { ...condition, ...patch } : condition
      )
    );
  };

  const addCondition = () => {
    onChange('conditions', [
      ...values.conditions,
      {
        key: uuidv4(),
        field: ConditionField.DURASI_MODEL,
        operator: ConditionOperator.LTE,
        value: '',
        values: [],
      },
    ]);
  };

  const removeCondition = (key: string) => {
    onChange(
      'conditions',
      values.conditions.filter((condition) => condition.key !== key)
    );
  };

  return (
    <FormCard title="Detail Package & Pricing">
      <div className="px-5 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <FieldLabel required>Package Name</FieldLabel>
            <input
              type="text"
              value={values.name}
              onChange={(e) => onChange('name', e.target.value)}
              placeholder="Platinum"
              aria-label="Package Name"
              className={inputClass(!!errors.name)}
            />
            <FieldError>{errors.name}</FieldError>
          </div>

          <div>
            <FieldLabel>Variant Type</FieldLabel>
            <SelectField
              value={values.type}
              onChange={(value) => onChange('type', value as RentType)}
              options={typeOptions}
              placeholder="Pilih variant"
              hasError={!!errors.type}
              ariaLabel="Variant Type"
            />
            <FieldError>{errors.type}</FieldError>
          </div>

          <div>
            <FieldLabel required>Model</FieldLabel>
            <SelectField
              value={values.modelID}
              onChange={(value) => onChange('modelID', value)}
              options={modelOptions}
              placeholder={
                isLoadingModels
                  ? 'Memuat model...'
                  : modelOptions.length
                    ? 'Pilih model'
                    : 'Tidak ada model tersedia'
              }
              hasError={!!errors.modelID}
              disabled={isLoadingModels || !modelOptions.length}
              ariaLabel="Model"
            />
            <FieldError>{errors.modelID}</FieldError>
          </div>

          <div>
            <FieldLabel required>Harga Sewa per Hari (Rp)</FieldLabel>
            <RupiahInput
              value={values.dailyPrice}
              onChange={(value) => onChange('dailyPrice', value)}
              placeholder="68000"
              hasError={!!errors.dailyPrice}
            />
            <FieldError>{errors.dailyPrice}</FieldError>
          </div>
        </div>

        <div className="mb-4">
          {/* Optional by design: a package with no condition accepts every
              unit of the model, which is a legitimate configuration. The
              empty state below says so out loud instead of leaving a gap. */}
          <FieldLabel>Condition</FieldLabel>
          {!values.conditions.length && (
            <div className="flex items-start gap-2 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2.5 text-[13px] text-gray-600">
              <i className="ri-information-line text-[14px] leading-[20px]"></i>
              <span>
                Tanpa condition, semua unit {selectedModelLabel} memenuhi
                syarat.
              </span>
            </div>
          )}
          <div className="space-y-2">
            {values.conditions.map((condition) => {
              const isClient = condition.field === ConditionField.CLIENT;
              const membership = isMembershipConditionField(condition.field)
                ? membershipDropdowns[condition.field]
                : null;

              return (
                <div key={condition.key} className="flex items-center gap-2">
                  <SelectField
                    value={condition.field}
                    onChange={(value) =>
                      // Switching between a range and a membership dimension
                      // clears the other half of the row: a kilometre bound is
                      // meaningless as a client list, and vice versa.
                      updateCondition(condition.key, {
                        field: value as ConditionField,
                        value: '',
                        values: [],
                      })
                    }
                    options={conditionFieldOptions}
                    className="flex-1"
                    ariaLabel="Condition field"
                  />
                  {membership ? (
                    <div className="flex-1">
                      <SearchableDropdown
                        multiple
                        options={membership.options}
                        value={condition.values}
                        onChange={(selected: (string | number)[]) =>
                          updateCondition(condition.key, {
                            values: selected.map(String),
                          })
                        }
                        placeholder={membership.placeholder}
                        emptyMessage={membership.emptyMessage}
                        loading={membership.loading}
                        // Client options already are the search result;
                        // filtering them again locally would hide matches the
                        // API did return.
                        disableLocalFilter={isClient}
                        onSearchChange={isClient ? onClientSearch : undefined}
                        error={!!errors.conditions}
                      />
                    </div>
                  ) : (
                    <>
                      <SelectField
                        value={condition.operator}
                        onChange={(value) =>
                          updateCondition(condition.key, {
                            operator: value as ConditionOperator,
                          })
                        }
                        options={operatorOptions}
                        className="w-[88px] shrink-0"
                        ariaLabel="Condition operator"
                      />
                      <div
                        className={`flex flex-1 items-center border rounded-lg px-3 focus-within:ring-1 focus-within:ring-primary ${
                          errors.conditions
                            ? 'border-red-500'
                            : 'border-gray-200'
                        }`}
                      >
                        <input
                          type="text"
                          inputMode="numeric"
                          value={condition.value}
                          onChange={(e) =>
                            updateCondition(condition.key, {
                              value: e.target.value.replace(/\D/g, ''),
                            })
                          }
                          aria-label="Condition value"
                          className="flex-1 py-2.5 text-[14px] text-gray-900 focus:outline-none"
                        />
                        <span className="text-[14px] text-gray-400 ml-2">
                          {CONDITION_FIELD_UNIT[condition.field]}
                        </span>
                      </div>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => removeCondition(condition.key)}
                    aria-label="Hapus condition"
                    className="w-9 h-9 flex items-center justify-center text-red-500 hover:text-red-600 shrink-0"
                  >
                    <i className="ri-delete-bin-line text-[18px]"></i>
                  </button>
                </div>
              );
            })}
          </div>
          <FieldError>{errors.conditions}</FieldError>
          <div className="mt-3">
            <AddRowButton label="Tambah" onClick={addCondition} />
          </div>
          <PackageStockPreviewNote values={values} onViewUnits={onViewUnits} />
        </div>

        <div className="mb-4">
          <FieldLabel>
            Gambar Package{' '}
            <span className="text-gray-400 font-normal">(opsional)</span>
          </FieldLabel>
          <ImageUpload
            value={values.imageURL || ''}
            title="Tambah gambar package"
            imageType={PACKAGE_IMAGE_UPLOAD_TYPE}
            recommendedDimensions={PACKAGE_IMAGE_DIMENSIONS}
            onUploaded={(url) => onChange('imageURL', url)}
            // Explicit null (not ''), so the API can tell "remove it" from
            // "not sent".
            onRemove={() => onChange('imageURL', null)}
            onUploadingChange={onUploadingChange}
          />
        </div>

        <div>
          <FieldLabel>Deskripsi Package</FieldLabel>
          <TextareaField
            value={values.description}
            onChange={(value) => onChange('description', value)}
            rows={4}
            placeholder="Jelaskan kondisi dan keunggulan package ini."
          />
        </div>
      </div>

      <div className="px-5 py-4 border-t border-gray-200">
        <ToggleRow
          label="Pre Order (PO)"
          helper="Jika dimatikan, tidak ada PO dan package langsung dibuat out of stock."
          checked={values.preOrderEnabled}
          onChange={(checked) => onChange('preOrderEnabled', checked)}
        />

        {values.preOrderEnabled && (
          <div>
            <FieldLabel>Max PO</FieldLabel>
            <input
              type="text"
              inputMode="numeric"
              value={values.maxPreorderCount}
              onChange={(e) =>
                onChange('maxPreorderCount', e.target.value.replace(/\D/g, ''))
              }
              aria-label="Max PO"
              className={inputClass(!!errors.maxPreorderCount)}
            />
            <FieldHelper>Maksimal jumlah PO yang dapat dibuat</FieldHelper>
            <FieldError>{errors.maxPreorderCount}</FieldError>
          </div>
        )}
      </div>
    </FormCard>
  );
};

export default PackageDetailSection;
